/*

? First, let's import the essentials - React, MUI and Styled

*/

import { LockOutlined } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { signOut } from "next-auth/react";
import React from "react";

/*

& Next, let's style the components

*/

const AccessDeniedBackground = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	minHeight: "100vh",
	padding: "2rem 1rem",
	background: "linear-gradient(135deg, #FFF8F2 0%, #FFFFFF 60%, #FFF8F2 100%)",
}));

const AccessDeniedCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	width: "100%",
	maxWidth: "28rem",
	padding: "3rem 2.25rem",
	background: "#FFFFFF",
	borderRadius: "1rem",
	boxShadow: "0 10px 40px rgba(176, 89, 17, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
	border: "1px solid rgba(0, 0, 0, 0.04)",
}));

const IconCircle = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: "5rem",
	height: "5rem",
	borderRadius: "50%",
	background: "linear-gradient(135deg, #FFE9DC 0%, #FFD2B5 100%)",
	marginBottom: "1.5rem",
	"& svg": {
		fontSize: "2.5rem",
		color: "#F76C35",
	},
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: "1.5rem",
	fontWeight: 600,
	color: "#212121",
	textAlign: "center",
	marginBottom: "0.5rem",
})) as typeof Typography;

const Subtitle = styled(Typography)(({ theme }) => ({
	fontSize: "0.9rem",
	fontWeight: 400,
	color: "#666666",
	textAlign: "center",
	lineHeight: 1.55,
	marginBottom: "2rem",
	maxWidth: "22rem",
})) as typeof Typography;

const SignOutButton = styled(Button)(({ theme }) => ({
	textTransform: "none",
	fontSize: "0.9rem",
	fontWeight: 500,
	padding: "0.65rem 1.75rem",
	borderRadius: "0.5rem",
	backgroundColor: "#F76C35",
	color: "#FFFFFF",
	border: "1px solid #F76C35",
	boxShadow: "none",
	width: "100%",
	"&:hover": {
		backgroundColor: "#E45A24",
		borderColor: "#E45A24",
		boxShadow: "0 4px 12px rgba(247, 108, 53, 0.25)",
	},
}));

const HelpNote = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	color: "#999999",
	textAlign: "center",
	marginTop: "1.5rem",
	maxWidth: "22rem",
	lineHeight: 1.5,
})) as typeof Typography;

/*

& Component

*/

const AccessDeniedPage = () => {
	const handleSignOut = () => {
		signOut({ callbackUrl: "/" });
	};

	return (
		<AccessDeniedBackground>
			<AccessDeniedCard>
				<IconCircle>
					<LockOutlined />
				</IconCircle>
				<Title
					variant="h1"
					component="h1"
				>
					Access denied
				</Title>
				<Subtitle
					variant="body2"
					component="p"
				>
					Your account isn&apos;t authorised to use the Beegru Control Panel. Contact an admin to request access, or sign out and try a different account.
				</Subtitle>
				<SignOutButton
					variant="contained"
					onClick={handleSignOut}
				>
					Sign out
				</SignOutButton>
				<HelpNote
					variant="caption"
					component="p"
				>
					Requires a Beegru marketplace account with control-panel access granted.
				</HelpNote>
			</AccessDeniedCard>
		</AccessDeniedBackground>
	);
};

/*

& Finally, let's export the component

*/

export default AccessDeniedPage;
